import React, { useRef, useMemo } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { useTheme } from "@/context/ThemeContext";

/**
 * A subtle, interactive 3D particle field that sits behind hero sections.
 *  - Camera parallax follows the mouse position (very lightly)
 *  - Colour palette adapts to light/dark theme
 *  - GPU-friendly: single Points object with ~1800 particles
 *  - Pointer-events: none so it never blocks clicks
 */

function ParticleField({ theme }) {
  const groupRef = useRef();
  const pointsRef = useRef();
  const mouse = useRef({ x: 0, y: 0 });

  // Palette per theme — vibrant in dark, muted in light
  const palette = theme === "dark"
    ? { primary: "#95D5B2", secondary: "#F4A261", accent: "#52B788" }
    : { primary: "#1B4332", secondary: "#95D5B2", accent: "#F4A261" };

  const { positions, colors } = useMemo(() => {
    const count = 1800;
    const pos = new Float32Array(count * 3);
    const col = new Float32Array(count * 3);
    const c1 = new THREE.Color(palette.primary);
    const c2 = new THREE.Color(palette.secondary);
    const c3 = new THREE.Color(palette.accent);
    for (let i = 0; i < count; i++) {
      // Distribute in a rough sphere with more density near centre
      const r = 8 + Math.random() * 10;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      pos[i * 3]     = r * Math.sin(phi) * Math.cos(theta);
      pos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      pos[i * 3 + 2] = r * Math.cos(phi) * 0.5; // flatten Z so it feels like a nebula
      const c = i % 3 === 0 ? c1 : i % 3 === 1 ? c2 : c3;
      col[i * 3]     = c.r;
      col[i * 3 + 1] = c.g;
      col[i * 3 + 2] = c.b;
    }
    return { positions: pos, colors: col };
    // eslint-disable-next-line
  }, [theme]);

  // Global mouse listener (bound once)
  React.useEffect(() => {
    const onMove = (e) => {
      const x = (e.clientX / window.innerWidth) * 2 - 1;
      const y = -(e.clientY / window.innerHeight) * 2 + 1;
      mouse.current.x = x;
      mouse.current.y = y;
    };
    window.addEventListener("pointermove", onMove);
    return () => window.removeEventListener("pointermove", onMove);
  }, []);

  useFrame((state, delta) => {
    if (!groupRef.current) return;
    // Gentle drift rotation
    groupRef.current.rotation.y += delta * 0.03;
    groupRef.current.rotation.x += delta * 0.01;
    // Mouse parallax — target then lerp for smoothness
    const targetX = mouse.current.y * 0.25;
    const targetY = mouse.current.x * 0.25;
    groupRef.current.rotation.x += (targetX - groupRef.current.rotation.x) * 0.03;
    groupRef.current.rotation.y += (targetY - groupRef.current.rotation.y) * 0.03;
  });

  return (
    <group ref={groupRef}>
      <points ref={pointsRef}>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" count={positions.length / 3} array={positions} itemSize={3} />
          <bufferAttribute attach="attributes-color" count={colors.length / 3} array={colors} itemSize={3} />
        </bufferGeometry>
        <pointsMaterial
          size={0.14}
          sizeAttenuation
          vertexColors
          transparent
          opacity={theme === "dark" ? 1 : 0.75}
          depthWrite={false}
          blending={THREE.AdditiveBlending}
        />
      </points>
    </group>
  );
}

export default function Hero3DBackground() {
  const { theme } = useTheme() || { theme: "light" };
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 overflow-hidden"
      style={{ minHeight: 500, zIndex: 0 }}
      data-testid="hero-3d-bg"
    >
      <Canvas
        camera={{ position: [0, 0, 10], fov: 65 }}
        dpr={[1, 1.5]}
        gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
      >
        <ParticleField theme={theme} />
      </Canvas>
    </div>
  );
}
